import * as core from '../core';
import * as manifest from './manifest';
import { actions, } from './store.actions';
import { RouteState, } from './store.constants';

const routeIsValid = (routeState) => routeState && routeState.validationState === RouteState.Valid;

export const getters = {

    /**
     * This determines whether the manifest service is available
     * @param {Object} state the vuex state
     * @returns {Boolean} whether the manifest service is available
     */
    getManifestServiceStatus(state) {
        return {
            status: state.manifestServiceStatus,
            message: state.manifestServiceErrorMessage,
        };
    },
    /**
     * This gets the manifest
     * @param {Object} state the vuex state
     * @returns {Object} the manifest
     */
    getFormStore(state) {
        return state.form;
    },
    /**
     * This gets the language
     * @param {Object} state the vuex state
     * @returns {String} the language
     */
    getFormLanguage(state) {
        return state.language;
    },
    /**
     * This gets the confirmation id
     * @param {Object} state the vuex state
     * @returns {String} the confirmation id
     */
    getFormConfirmationId(state) {
        return state.confirmationId;
    },
    /**
     * This gets a function allowing calculation of a dynamic id for the route
     * @param {Object} state the vuex state
     * @param {String} route the route
     * @returns {Function} function allowing calculation of a dynamic id for the route
     */
    // eslint-disable-next-line no-unused-vars
    getRouteStateId(state, route) {
        return (route) => {
            let routeState = state.routeStateDictionary.find((r) => r.route === route);
            return routeState ? routeState.id : null;
        };
    },
    /**
     * This gets the route array
     * @param {Object} state the vuex state
     * @returns {Object} the route array
     */
    getRouteArray(state) {
        return state.routeArray;
    },
    /**
     * This gets whether an attempt to submit the form has been made
     * @param {Object} state the vuex state
     * @returns {Boolean} whether an attempt to submit the form has been made
     */
    submitFormHasBeenAttempted(state) {
        return state.submitFormHasBeenAttempted;
    },
    /**
     * This gets whether a submit is allowed at this time
     * @param {Object} state the vuex state
     * @returns {Boolean} whether a submit is allowed at this time
     */
    canSubmit(state) {
        return manifest.canSubmit(state.form);
    },

    /**
     * This gets a function allowing calculation of the errors for a route
     * @param {Object} state the vuex state
     * @param {String} route the route
     * @returns {Function} function allowing calculation of the errors for a route
     */
    // eslint-disable-next-line no-unused-vars
    showRouteErrors(state, route) {
        return (route) => state.routeStateDictionary.find((r) => r.route === route) !== undefined;
    },
    /**
     * This gets a function allowing calculation of whether you can navigate from a route
     * @param {Object} state the vuex state
     * @param {String} route the route
     * @returns {Function} function allowing calculation of whether you can navigate from a route
     */
    // eslint-disable-next-line no-unused-vars
    canNavigateFromRoute(state, route) {
        return (route) => !manifest.routeHasErrors(state.form, route);
    },

    /**
     * This determines whether the navigation link on the progress bar will be active
     * @param {Object} state the vuex state
     * @param {String} routeToCheck the route to check
     * @param {String} currentRoute the current route
     * @returns {Boolean} whether it is allowed to try to navigate to the specified route
     */
    // TODO: when we pull the custom interactions out of the
    // Abstract classes, this can be refactored into a custom interaction.
    // eslint-disable-next-line no-unused-vars
    canNavigateToRoute(state, routeToCheck, currentRoute) {
        return (routeToCheck, currentRoute) => {
            let complaintInfoState = state.routeStateDictionary
                .find((r) => r.route === '/complaint-info');

            let contactInfoState = state.routeStateDictionary
                .find((r) => r.route === '/contact-info');

            if (routeToCheck === '/contact-info') {
                if (!routeIsValid(complaintInfoState)) {
                    return false;
                }
            }

            if (routeToCheck === '/confirm-submit') {
                if (currentRoute === '/contact-info') {
                    return true;
                }

                if (!routeIsValid(complaintInfoState) || !routeIsValid(contactInfoState)) {
                    return false;
                }
            }

            return true;
        };
    },

    /**
     * This gets a manifest node
     * @param {Object} state the vuex state
     * @param {String} id the id
     * @returns {Boolean} manifest node
     */
    // eslint-disable-next-line no-unused-vars
    getManifestNode(state, id) {
        return (id) => manifest.getNodeById(state.form, id);
    },
};

export const mutations = (vue) => ({
    SET_ROUTER_IS_INITIALIZED: (state) => {
        state.routerIsInitialized = true;
    },
    SET_ROUTE_ARRAY: (state, {
        routeArray,
    }) => {
        state.routeArray = routeArray;
    },
    SET_LANGUAGE: (state, {
        language,
    }) => {
        state.language = language;
    },
    SET_SUBMIT_FORM_HAS_BEEN_ATTEMPTED: (state, {
        submitFormHasBeenAttempted,
    }) => {
        state.submitFormHasBeenAttempted = submitFormHasBeenAttempted;
    },
    SET_CONFIRMATION_ID: (state, {
        confirmationId,
        confirmationGuid,
    }) => {
        state.confirmationId = confirmationId;
        state.confirmationGuid = confirmationGuid;
    },
    SET_MANIFEST_SERVICE_STATUS: (state, {
        manifestServiceStatus,
        errorMessage,
    }) => {
        state.manifestServiceStatus = manifestServiceStatus;
        state.manifestServiceErrorMessage = errorMessage || '';
    },
    SET_FORM: (state, {
        form, formName, formUrl, formGuid,
    }) => {
        state.formName = formName || state.formName;
        state.formUrl = formUrl || state.formUrl;
        state.formGuid = formGuid || state.formGuid;
        state.form = form;
    },
    UPDATE_VALUE: (state, {
        id, valuesObject,
    }) => {
        let node = manifest.getNodeById(state.form, id);
        if (node) {
            vue.set(node, 'values', valuesObject);
        }
    },
    UPDATE_ERROR: (state, {
        id, errorMessageObject, childId,
    }) => {
        let node = manifest.getNodeById(state.form, id);
        if (node) {
            // reset the flag initiating a manual update
            // This flag is set if the update is triggered by custom validation generally
            node.shouldUpdateError = null;
            manifest.updateNodeError(node, errorMessageObject, childId);
            manifest.updateGlobalError(state.form);
            manifest.populateRouteErrorListFromControlId(state.form, id);
        }
    },
    SET_ROUTE_VALIDATION_STATE: (state, {
        route,
        validationState,
    }) => {
        let routeState = state.routeStateDictionary.find((r) => r.route === route);
        if (routeState) {
            routeState.validationState = validationState;
        } else {
            state.routeStateDictionary.push({
                route,
                id: null,
                validationState,
            });
        }
    },
    AUTHORIZE_NAVIGATE_FROM_ROUTE: (state, {
        route,
        id,
    }) => {
        let routeState = state.routeStateDictionary.find((r) => r.route === route);
        if (routeState) {
            routeState.id = id;
        } else {
            state.routeStateDictionary.push({
                route,
                id,
            });
        }
    },
});

/**
 * Creates the formModule of the Vuex store.
 * @param {any} vue The Vue Instance.
 * @returns {any} The FormModule.
 */
// eslint-disable-next-line arrow-body-style
const formModule = (vue) => {
    return {
        state: {
            manifestServiceStatus: '',
            manifestServiceErrorMessage: '',
            routerIsInitialized: false,
            routeArray: null,
            routeStateDictionary: [],
            submitFormHasBeenAttempted: false,
            formName: null,
            formUrl: null,
            formGuid: null,
            form: {},
            confirmationId: null,
            confirmationGuid: null,
            language: 'en-us',
        },
        mutations: mutations(vue),
        getters,
        actions,
    };
};

/**
 * Creates the vuex store
 *  @param {*} vue The Vue Instance.
 *  @param {*} vuex The Vuex Instance.
 *  @returns {*} The vuex store.
 */
export const createStore = () => {
    const state = {};
    return new core.Vuex.Store({
        modules: {
            formModule: formModule(core.Vue),
        },
        state,
    });
};



// WEBPACK FOOTER //
// ./source/framework/instance/store.js